import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { auth } from '../services/auth';
import 'xterm/css/xterm.css';
import config from '../config';

// Temporary workaround for server-side winsize SID behavior
const DISABLE_WINSIZE = true;

const RttyTerminal = ({ rttySession, onError, deviceConnected, fullscreen = false, onConnectionChange, onFileMessage, onTerminalReady, suppressTerminalOutput = false, onTerminalText = null }) => {
  const suppressionRef = useRef(false);
  
  // Sync prop to ref immediately (no render delay)
  suppressionRef.current = suppressTerminalOutput;
  
  const shouldSuppressText = useCallback((text) => {
    if (!text) return false;
    if (!suppressionRef.current) return false; // don't suppress when not in transfer mode
    
    // Log all terminal output during file transfer for debugging
    console.log('[RTTY Terminal] Received text during transfer:', text);
    
    // NEVER suppress error messages - these are critical for detecting failures
    const errorPatterns = [
      /failed:/i,
      /no such file/i,
      /file not found/i,
      /cannot find/i,
      /does not exist/i,
      /error/i,
      /failed to open/i,
      /open.*failed/i,
    ];
    
    const hasError = errorPatterns.some(pattern => pattern.test(text));
    if (hasError) {
      console.warn('[RTTY Terminal] Error detected, NOT suppressing:', text);
      // Notify parent component about the error
      if (onTerminalText) {
        onTerminalText(text);
      }
      return false; // Don't suppress error messages
    }
    
    // Check if text contains only transfer-related patterns
    const progRe = /^\s*\d+%\s+\S+\s+\S+/;
    const suppressRe = [
      /rtty\s+-s\s+/i,
      /rtty\s+-r\s*/i,
      /transferring\s+'.*'\.{3}/i,
      /press\s+ctrl\+c\s+to\s+cancel/i,
      /waiting\s+to\s+receive/i,
      /sudo.*not\s+found/i,
      progRe,
    ];
    
    // Check if ALL non-empty lines match suppress patterns
    let hasContent = false;
    let allSuppressed = true;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char !== '\r' && char !== '\n' && char !== ' ' && char !== '\t') {
        hasContent = true;
        // Found non-whitespace content, check if this text should be suppressed
        const matchesAny = suppressRe.some(re => re.test(text));
        if (!matchesAny) {
          allSuppressed = false;
          console.log('[RTTY Terminal] Text does not match suppress patterns, will be shown:', text);
          break;
        }
      }
    }
    
    const shouldSuppress = hasContent && allSuppressed;
    if (shouldSuppress) {
      console.log('[RTTY Terminal] Suppressing text:', text);
      
      // Extract progress percentage from suppressed text (e.g., "16%    3.62 MB     46.279s")
      // The format is: percentage% followed by whitespace, then size, then time
      const progressMatch = text.match(/(\d+)%\s+/);
      if (progressMatch && onTerminalText) {
        const percentage = parseInt(progressMatch[1], 10);
        if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
          console.log('[RTTY Terminal] Extracted progress:', percentage + '%');
          // Notify parent about progress update
          onTerminalText({ type: 'progress', percentage });
        }
      }
    }
    
    return shouldSuppress;
  }, [onTerminalText]);

  const filterText = useCallback((text) => {
    if (!text) return text;
    if (!suppressionRef.current) return text; // no filtering when not suppressing
    
    console.log('[RTTY Terminal] Filtering text:', text);
    
    // NEVER filter out error messages - these are critical
    const errorPatterns = [
      /failed:/i,
      /no such file/i,
      /file not found/i,
      /cannot find/i,
      /does not exist/i,
      /error/i,
      /failed to open/i,
      /open.*failed/i,
    ];
    
    // Filter out transfer-related patterns line by line, preserving line endings
    const suppressRe = [
      /rtty\s+-[rs]/i,                      // "rtty -R" or "rtty -S"
      /sudo.*rtty/i,                        // "sudo -n rtty" or "sudo: not found" with rtty
      /transferring\s+/i,                   // "Transferring 'file'..."
      /press\s+ctrl/i,                      // "Press Ctrl+C to cancel"
      /waiting\s+to\s+receive/i,            // "Waiting to receive"
      /\d+%\s+\d+/,                         // Progress like "100%    653 B"
      /-ash:\s+sudo:\s+not\s+found/i,      // "-ash: sudo: not found"
      /sudo:\s+not\s+found/i,               // "sudo: not found"
      /\|\|\s+rtty\s+-[rs]/i,               // "|| rtty -R" or "|| rtty -S"
    ];
    
    // Process line by line, keeping line endings intact
    const lines = [];
    let start = 0;
    
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '\n') {
        const line = text.substring(start, i + 1);
        // Check if this is an error message first
        const isError = errorPatterns.some(pattern => pattern.test(line));
        if (isError) {
          console.warn('[RTTY Terminal] Error line detected, keeping:', line);
          lines.push(line);
          // Notify parent about error
          if (onTerminalText) {
            onTerminalText(line);
          }
        } else {
          const shouldSuppress = suppressRe.some(re => re.test(line));
          if (shouldSuppress) {
            console.log('[RTTY Terminal] Filtering out line:', line);
            // Extract progress percentage from suppressed line (e.g., "16%    3.62 MB     46.279s")
            const progressMatch = line.match(/(\d+)%\s+/);
            if (progressMatch && onTerminalText) {
              const percentage = parseInt(progressMatch[1], 10);
              if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
                console.log('[RTTY Terminal] Extracted progress from filtered line:', percentage + '%');
                // Notify parent about progress update
                onTerminalText({ type: 'progress', percentage });
              }
            }
          } else {
            console.log('[RTTY Terminal] Keeping line (not suppressed):', line);
            lines.push(line);
          }
        }
        start = i + 1;
      }
    }
    
    // Handle any remaining text without line ending
    if (start < text.length) {
      const line = text.substring(start);
      const isError = errorPatterns.some(pattern => pattern.test(line));
      if (isError) {
        console.warn('[RTTY Terminal] Error text detected, keeping:', line);
        lines.push(line);
        // Notify parent about error
        if (onTerminalText) {
          onTerminalText(line);
        }
      } else {
        const shouldSuppress = suppressRe.some(re => re.test(line));
        if (shouldSuppress) {
          console.log('[RTTY Terminal] Filtering out remaining text:', line);
          // Extract progress percentage from suppressed text (e.g., "16%    3.62 MB     46.279s")
          const progressMatch = line.match(/(\d+)%\s+/);
          if (progressMatch && onTerminalText) {
            const percentage = parseInt(progressMatch[1], 10);
            if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
              console.log('[RTTY Terminal] Extracted progress from remaining text:', percentage + '%');
              // Notify parent about progress update
              onTerminalText({ type: 'progress', percentage });
            }
          }
        } else {
          console.log('[RTTY Terminal] Keeping remaining text (not suppressed):', line);
          lines.push(line);
        }
      }
    }
    
    const filtered = lines.join('');
    console.log('[RTTY Terminal] Filtered result:', filtered);
    return filtered;
  }, [onTerminalText]);
  const terminalRef = useRef(null);
  const socketRef = useRef(null);
  const connectingRef = useRef(false);
  const sessionId = rttySession?.connection_id;
  const sessionToken = rttySession?.token;
  const [terminal, setTerminal] = useState(null);
  const [, setFitAddon] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);
  const isReady = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const lastInputTimeRef = useRef(Date.now()); // Legacy variable, kept for compatibility
  const resizeObserverRef = useRef(null);
  const focusTimeRef = useRef(0);
  const lastResponseTimeRef = useRef(Date.now());
  const responseCheckIntervalRef = useRef(null);
  const resizeScheduledRef = useRef(false);
  const lastSizeRef = useRef({ cols: 0, rows: 0 });
  const suppressInputUntilRef = useRef(0);
  const writeQueueRef = useRef([]);
  const isFlushingRef = useRef(false);
  const decoderRef = useRef(typeof TextDecoder !== 'undefined' ? new TextDecoder() : null);
  const inputHandlerRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const inputProcessingRef = useRef(false); // Legacy variable, kept for compatibility

  // When suppression toggles on, allow queue to continue processing (filtering will happen in flush)
  // Removed: don't clear queue - let terminal control sequences through
  const isAltNullAscii = useCallback((arr) => {
    if (!arr || arr.length < 4 || (arr.length % 2) !== 0) return false;
    // Drop short sequences of alternating NUL + ASCII (e.g., UTF-16-like echoes)
    const MAX_LEN = 16;
    if (arr.length > MAX_LEN) return false;
    for (let i = 0; i < arr.length; i += 2) {
      if (arr[i] !== 0) return false;
      const b = arr[i + 1];
      if (b >= 0x80) return false;
    }
    return true;
  }, []);
  const isCaretGarbage = useCallback((arr) => {
    if (!arr || arr.length === 0) return false;
    let caretCount = 0;
    for (let i = 0; i < arr.length; i += 1) {
      if (arr[i] === 0x5e) caretCount += 1; // '^'
    }
    // Drop short bursts dominated by caret notation like "^@^E^@g^@,"
    return caretCount >= 3 && arr.length <= 32;
  }, []);
  const stripAltNullAsciiBursts = useCallback((arr) => {
    if (!arr || arr.length < 4) return arr;
    const out = new Uint8Array(arr.length);
    let outLen = 0;
    let i = 0;
    while (i < arr.length) {
      if (i + 3 < arr.length && arr[i] === 0x00) {
        // detect short alternating 0x00 + ASCII bursts (length up to 8 bytes)
        let j = i;
        let pairs = 0;
        while (j + 1 < arr.length && arr[j] === 0x00 && arr[j + 1] < 0x80 && pairs < 8) {
          j += 2;
          pairs += 1;
        }
        const burstLen = j - i;
        if (pairs >= 2 && burstLen <= 16) {
          // skip this burst
          i = j;
          continue;
        }
      }
      out[outLen++] = arr[i++];
    }
    return outLen === arr.length ? arr : out.slice(0, outLen);
  }, []);

  // Cleanup function to properly close WebSocket
  const cleanupWebSocket = useCallback(() => {
    if (socketRef.current) {
      try {
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.close(1000, 'Cleanup');
        }
        socketRef.current = null;
      } catch (error) {
        console.error('Error cleaning up WebSocket:', error);
      }
    }
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  // Initialize terminal
  useEffect(() => {
    if (!terminalRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#ffffff',
      },
      convertEol: false, // Don't convert line endings - let the remote system handle it
      disableStdin: false, // Allow input
      cursorStyle: 'block',
      allowTransparency: false,
      scrollback: 100000,
    });

    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());

    term.open(terminalRef.current);
    term.focus();
    
    // Handle focus events to prevent spurious input when dev tools open/close
    term.textarea.addEventListener('blur', () => {
      // Clear any pending input when focus is lost
      if (term.textarea) {
        term.textarea.value = '';
      }
    });
    
    term.textarea.addEventListener('focus', () => {
      focusTimeRef.current = Date.now();
      suppressInputUntilRef.current = Date.now() + 150;
      // Clear any stuck input when focus is regained
      if (term.textarea) {
        term.textarea.value = '';
      }
      // Force a refresh of the terminal to clear any stuck state
      setTimeout(() => {
        term.refresh(0, term.rows - 1);
      }, 50);
      
      // Send a heartbeat to check if the device is still connected
      setTimeout(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({ type: 'heartbeat_check' }));
        }
      }, 100);
    });
    
    // Create a ResizeObserver to handle container resizing
    resizeObserverRef.current = new ResizeObserver(() => {
      if (fit) {
        // Temporarily suppress input around resizes (e.g., when DevTools opens)
        suppressInputUntilRef.current = Date.now() + 400;
        if (resizeScheduledRef.current) return;
        resizeScheduledRef.current = true;
        requestAnimationFrame(() => {
          try {
            fit.fit();
            // Temporarily disable winsize messages until server fix is deployed
            if (!DISABLE_WINSIZE) {
              if (socketRef.current?.readyState === WebSocket.OPEN) {
                const dimensions = fit.proposeDimensions();
                if (dimensions) {
                  const changed =
                    dimensions.cols !== lastSizeRef.current.cols ||
                    dimensions.rows !== lastSizeRef.current.rows;
                  if (changed) {
                    lastSizeRef.current = { cols: dimensions.cols, rows: dimensions.rows };
                    socketRef.current.send(JSON.stringify({
                      type: 'winsize',
                      cols: dimensions.cols,
                      rows: dimensions.rows
                    }));
                  }
                }
              }
            }
          } catch (err) {
            console.error('Error fitting terminal:', err);
          } finally {
            resizeScheduledRef.current = false;
          }
        });
      }
    });

    // Also listen to window resize to clear any pending input
    const onWindowResize = () => {
      suppressInputUntilRef.current = Date.now() + 400;
      if (term.textarea) {
        term.textarea.value = '';
      }
    };
    window.addEventListener('resize', onWindowResize);

    // Observe the terminal's container
    if (terminalRef.current) {
      resizeObserverRef.current.observe(terminalRef.current);
    }

    setTerminal(term);
    setFitAddon(fit);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', onWindowResize);
      if (responseCheckIntervalRef.current) {
        clearInterval(responseCheckIntervalRef.current);
      }
      cleanupWebSocket();
      term.dispose();
    };
  }, [cleanupWebSocket]);

  // Handle WebSocket connection
  useEffect(() => {
    if (!terminal || !sessionId || !deviceConnected || isConnected || isConnecting || isTerminated) return;
    if (connectingRef.current) return;

    const connectWebSocket = async () => {
      try {
        setIsConnecting(true);
        connectingRef.current = true;
        console.log('Connecting to RTTY session:', sessionId);

        // Clean up any existing connection
        cleanupWebSocket();

        // Use the original working URL format
        const wsUrl = `wss://${config.apiBaseUrl.replace('https://', '')}/${config.apiPathUrl}/websocket_rtty?session=${sessionId}&access_token=${auth.getToken()}`;
        console.log('Connecting to WebSocket:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        socketRef.current = ws;

        // Set connection timeout
        const connectionTimeout = setTimeout(() => {
          if (ws.readyState !== WebSocket.OPEN) {
            console.log('Connection timeout, closing socket');
            ws.close();
          }
        }, 60000); // 60 second timeout

        ws.onopen = () => {
          clearTimeout(connectionTimeout);
          setIsConnected(true);
          setIsConnecting(false);
          connectingRef.current = false;
          terminal.writeln('\x1b[1;32mConnected to RTTY server\x1b[0m');
          
          // Notify parent of connection status
          if (onConnectionChange) {
            onConnectionChange(true);
          }
          
          // Send initial token message
          const tokenMessage = JSON.stringify({ token: sessionToken });
          ws.send(tokenMessage);
          
          // Provide sendCommand function to parent
          if (onTerminalReady) {
            const sendCommand = (command, raw = false) => {
              if (ws.readyState === WebSocket.OPEN) {
                const encoder = new TextEncoder();
                // If raw=true, send command as-is without adding newline
                const data = raw ? encoder.encode(command) : encoder.encode(command + '\n');
                ws.send(data);
                return true;
              }
              return false;
            };
            onTerminalReady(sendCommand, terminal);
          }
          
          // Start response timeout checker
          responseCheckIntervalRef.current = setInterval(() => {
            const timeSinceLastResponse = Date.now() - lastResponseTimeRef.current;
            if (timeSinceLastResponse > 30000) { // 30 seconds
              terminal.write('\r\n\x1b[33m[Warning: Device may be disconnected. Try pressing Enter to test.]\x1b[0m\r\n');
              // Reset the timer to avoid spam
              lastResponseTimeRef.current = Date.now() - 25000;
            }
          }, 5000);
        };

        ws.onmessage = (event) => {
          try {
            // Update last response time whenever we receive any message
            lastResponseTimeRef.current = Date.now();
            
            if (typeof event.data === 'string') {
              try {
                const data = JSON.parse(event.data);
                
                if (data.type === 'login') {
                  if (data.error === 0) {
                    terminal.writeln('\r\n\x1b[32mLogin successful\x1b[0m');
                  } else {
                    terminal.writeln('\r\n\x1b[31mLogin failed: ' + data.error + '\x1b[0m');
                    onError && onError('Login failed: ' + data.error);
                    setIsTerminated(true);
                  }
                } else if (data.type === 'session') {
                  if (data.device_id) {
                    terminal.writeln('\r\n\x1b[32mConnected to device: ' + data.device_id + '\x1b[0m');
                  } else {
                    terminal.writeln('\r\n\x1b[33mWaiting for device connection...\x1b[0m');
                  }
                } else if (data.type === 'heartbeat') {
                  // Respond to RTTY protocol heartbeat
                  ws.send(JSON.stringify({ type: 'heartbeat_response' }));
                } else if (data.type === 'ping') {
                  // Respond to RTTY protocol ping (in addition to WebSocket level pings)
                  ws.send(JSON.stringify({ type: 'pong' }));
                } else if (data.type === 'file') {
                  // Handle file transfer messages
                  if (onFileMessage) {
                    onFileMessage(data);
                  }
                } else if (data.error) {
                  console.error('RTTY: Error from server:', data.error);
                  terminal.writeln('\r\n\x1b[31mError: ' + data.error + '\x1b[0m');
                  onError && onError(data.error);
                  setIsTerminated(true);
                }
              } catch (e) {
                // If not JSON, it might still be a binary protocol frame mis-typed as text.
                // Detect and drop non-terminal RTTY frames (e.g., winsize) or extract terminal payload.
                let s = event.data;
                const bytes = new Uint8Array(s.length);
                for (let i = 0; i < s.length; i += 1) bytes[i] = s.charCodeAt(i) & 0xff;
                // removed temporary [RTTY][text] debug logging
                if (isAltNullAscii(bytes)) return;
                const stripped = stripAltNullAsciiBursts(bytes);
                // Strip leading NULs
                let start = 0;
                while (start < stripped.length && stripped[start] === 0x00) start += 1;
                const noLeadingNull = start > 0 ? stripped.slice(start) : stripped;
                if (bytes.length >= 3) {
                  const typeByte = bytes[0];
                  const lenField = (bytes[1] << 8) | bytes[2];
                  const fullFrame = bytes.length === 3 + lenField;
                  const isKnownType = typeByte >= 0x00 && typeByte <= 0x09;
                  if (fullFrame && isKnownType) {
                    if (typeByte === 0x03 && bytes.length >= 4) {
                      // Terminal data frame: strip header and (optionally) suppress/filter before writing
                      const payload = noLeadingNull.slice(4);
                      const text = decoderRef.current
                        ? decoderRef.current.decode(payload, { stream: true })
                        : String.fromCharCode.apply(null, payload);
                      if (text) {
                        if (shouldSuppressText(text)) return;
                        const out = filterText(text);
                        if (out) terminal.write(out);
                      }
                    }
                    // Drop non-terminal frames
                    return;
                  }
                }
                // Treat remaining as regular terminal text (strip leading NULs without regex)
                let k = 0;
                while (k < s.length && s.charCodeAt(k) === 0) k++;
                const text = decoderRef.current ? decoderRef.current.decode(noLeadingNull, { stream: true }) : s.slice(k);
                if (text) {
                  if (shouldSuppressText(text)) return;
                  const out = filterText(text);
                  if (out) terminal.write(out);
                }
              }
            } else {
              // Handle binary data with a write queue to avoid UI stalls on long outputs
              const isRttyFrame = (arr) => {
                if (!arr || arr.length < 3) return false;
                const typeByte = arr[0];
                const lenField = (arr[1] << 8) | arr[2];
                if (arr.length !== 3 + lenField) return false;
                // Known RTTY types 0x00..0x09; only 0x03 (TermData) is terminal output
                if (typeByte >= 0x00 && typeByte <= 0x09 && typeByte !== 0x03) return true;
                return false;
              };

              const maybeExtractTermData = (arr) => {
                // If it's a full TermData frame [0x03][len][sid][data...], strip header
                if (!arr || arr.length < 4) return arr;
                const typeByte = arr[0];
                const lenField = (arr[1] << 8) | arr[2];
                const full = arr.length === 3 + lenField;
                if (full && typeByte === 0x03) {
                  return arr.slice(4);
                }
                return arr;
              };

              const enqueue = (arr) => {
                // Allow data to flow even during suppression (filtering happens in text conversion)
                //if (isAltNullAscii(arr)) return;
                //arr = stripAltNullAsciiBursts(arr);
                if (isRttyFrame(arr)) return; // drop non-terminal RTTY frames like winsize
                arr = maybeExtractTermData(arr);
                // Strip leading NULs
                while (arr.length > 0 && arr[0] === 0x00) {
                  arr = arr.slice(1);
                }
                if (isCaretGarbage(arr)) return;
                isReady.current = true;
                writeQueueRef.current.push(arr);
                
                // Monitor queue depth during long outputs
                if (writeQueueRef.current.length > 100 && writeQueueRef.current.length % 50 === 0) {
                }

                // Coalesce small chunks if queue grows large to reduce write calls
                const q = writeQueueRef.current;
                if (q.length > 512) {
                  let totalLen = 0;
                  const buffers = [];
                  // Merge up to 64 small buffers but cap total to ~64KB
                  while (buffers.length < 64 && q.length > 0 && q[0].length <= 2048 && (totalLen + q[0].length) <= 65536) {
                    const b = q.shift();
                    if (!b) break;
                    buffers.push(b);
                    totalLen += b.length;
                  }
                  if (buffers.length > 1) {
                    const merged = new Uint8Array(totalLen);
                    let offset = 0;
                    for (const b of buffers) {
                      merged.set(b, offset);
                      offset += b.length;
                    }
                    q.unshift(merged);
                  } else if (buffers.length === 1) {
                    q.unshift(buffers[0]);
                  }
                }
                if (!isFlushingRef.current) {
                  isFlushingRef.current = true;
                  const flush = () => {
                    const sliceStart = performance.now();
                    const FRAME_BUDGET_MS = 6;
                    const CHUNK_SIZE = 8192;
                    let partsProcessed = 0;
                    const textParts = [];

                    while (writeQueueRef.current.length > 0) {
                      if (performance.now() - sliceStart > FRAME_BUDGET_MS) break;
                      const chunk = writeQueueRef.current.shift();
                      if (!chunk) continue;

                      if (chunk.length > CHUNK_SIZE) {
                        for (let i = 0; i < chunk.length; i += CHUNK_SIZE) {
                          const part = chunk.subarray(i, Math.min(i + CHUNK_SIZE, chunk.length));
                          if (decoderRef.current) {
                            const text = decoderRef.current.decode(part, { stream: true });
                            if (text) textParts.push(text);
                          } else {
                            let text = '';
                            for (let j = 0; j < part.length; j += 1) text += String.fromCharCode(part[j]);
                            if (text) textParts.push(text);
                          }
                        }
                      } else {
                        if (decoderRef.current) {
                          const text = decoderRef.current.decode(chunk, { stream: true });
                          if (text) textParts.push(text);
                        } else {
                          let text = '';
                          for (let j = 0; j < chunk.length; j += 1) text += String.fromCharCode(chunk[j]);
                          if (text) textParts.push(text);
                        }
                      }

                      partsProcessed += 1;
                      if (partsProcessed >= 256 && performance.now() - sliceStart > FRAME_BUDGET_MS) break;
                    }

                    if (textParts.length > 0) {
                      let out = textParts.join('');
                      if (shouldSuppressText(out)) {
                        out = '';
                      } else {
                        out = filterText(out);
                      }
                      if (out) terminal.write(out);
                    }

                    if (writeQueueRef.current.length > 0) {
                      if (typeof requestAnimationFrame === 'function') {
                        requestAnimationFrame(flush);
                      } else {
                        setTimeout(flush, 0);
                      }
                    } else {
                      if (decoderRef.current) {
                        const remainder = decoderRef.current.decode();
                        if (remainder) terminal.write(remainder);
                      }
                      isFlushingRef.current = false;
                    }
                  };
                  if (typeof requestAnimationFrame === 'function') {
                    requestAnimationFrame(flush);
                  } else {
                    setTimeout(flush, 0);
                  }
                }
              };

              if (event.data instanceof ArrayBuffer) {
                enqueue(new Uint8Array(event.data));
              } else if (event.data instanceof Blob) {
                const blob = event.data;
                blob.arrayBuffer().then(buffer => {
                  enqueue(new Uint8Array(buffer));
                }).catch(error => {
                  console.error('Failed to read binary data:', error);
                  onError && onError('Failed to read terminal data');
                  setIsTerminated(true);
                });
              }
            }
          } catch (error) {
            console.error('Error handling message:', error);
          }
        };

        ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          setIsConnected(false);
          setIsConnecting(false);
          setIsTerminated(true);
          connectingRef.current = false;

          terminal.writeln('\r\n\x1b[1;31mConnection closed\x1b[0m');
          terminal.writeln('\r\n\x1b[1;33mSession ended. Please close this tab and start a new session if needed.\x1b[0m');
          
          // Notify parent about disconnection
          if (onConnectionChange) {
            onConnectionChange(false);
          }
          
          // Notify parent component about closure
          onError && onError('RTTY session ended');
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          clearTimeout(connectionTimeout);
          setIsConnected(false);
          setIsConnecting(false);
          setIsTerminated(true);
          connectingRef.current = false;
          terminal.writeln('\x1b[1;31mConnection error\x1b[0m');
          terminal.writeln('\r\n\x1b[1;33mPlease close this tab and try again.\x1b[0m');
        };

        // Handle terminal input: send raw data immediately, let xterm handle history/editing
        inputHandlerRef.current = (data) => {
          if (ws.readyState !== WebSocket.OPEN || !isReady.current) return;
          const encoder = new TextEncoder();
          ws.send(encoder.encode(data));
        };
        
        terminal.onData(inputHandlerRef.current);

      } catch (error) {
        console.error('Error establishing WebSocket connection:', error);
        setIsConnected(false);
        setIsConnecting(false);
        setIsTerminated(true);
        terminal.writeln('\x1b[1;31mFailed to establish connection\x1b[0m');
        terminal.writeln('\r\n\x1b[1;33mPlease close this tab and try again.\x1b[0m');
        if (onError) {
          onError('Failed to establish connection');
        }
      }
    };

    connectWebSocket();
  }, [terminal, sessionId, sessionToken, deviceConnected, isConnected, isConnecting, isTerminated, cleanupWebSocket, isAltNullAscii, isCaretGarbage, stripAltNullAsciiBursts, onError, onConnectionChange, onFileMessage, onTerminalReady, filterText, shouldSuppressText]);

  return (
    <Box 
      sx={{ 
        width: '100%',
        height: fullscreen ? '100%' : '400px',
        bgcolor: '#1e1e1e',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        ref={terminalRef}
        onClick={() => terminal && terminal.focus()}
        sx={{
          flex: 1,
          '& .xterm': {
            height: '100%',
            '& .xterm-viewport': {
              height: '100% !important'
            }
          }
        }}
      />
    </Box>
  );
};

export default RttyTerminal; 